import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Avatar,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Typography,
  IconButton,
  useTheme,
} from "@mui/material";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import LogoutIcon from "@mui/icons-material/Logout";
import SettingsIcon from "@mui/icons-material/Settings";
import DashboardIcon from "@mui/icons-material/Dashboard";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useAuth } from "../../context/AuthContext";

const ProfileDropDown: React.FC = () => {
  const theme = useTheme();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();

  // State for the profile menu
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  // Open the menu
  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  // Close the menu
  const handleClose = () => {
    setAnchorEl(null);
  };

  // Derive role-based base path
  const role = user?.["custom:role"] || user?.role || "";
  const basePath = role === "TEACHER" ? "/teacher" : role === "STUDENT" ? "/student" : "/admin";

  // Handle profile navigation
  const handleProfile = () => {
    navigate(`${basePath}/profile`);
    handleClose();
  };

  // Handle settings navigation
  const handleSettings = () => {
    navigate(`${basePath}`);
    handleClose();
  };

  // Handle dashboard navigation
  const handleDashboard = () => {
    navigate(`${basePath}`);
    handleClose();
  };

  // Handle sign out
  const handleSignOut = () => {
    signOut();
    handleClose();
    navigate("/login");
  };

  // Get user initials for avatar
  const getUserInitials = (): string => {
    if (!user || !user.name) return "?";

    const nameParts = user.name.split(" ");
    if (nameParts.length >= 2) {
      return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
    }
    return nameParts[0][0].toUpperCase();
  };

  return (
    <Box sx={{ display: "flex", alignItems: "center" }}>
      {/* User name (hidden on small screens) */}
      <Typography
        variant="body1"
        sx={{
          mr: 1.5,
          display: { xs: "none", sm: "block" },
        }}
      >
        {user?.name || "User"}
      </Typography>

      {/* User avatar and dropdown trigger */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          "&:hover": {
            opacity: 0.9,
          },
        }}
        onClick={handleMenu}
      >
        {user?.avatar ? (
          <Avatar
            src={user.avatar}
            alt={user?.name || "User"}
            sx={{ width: 36, height: 36 }}
          />
        ) : (
          <Avatar
            sx={{
              width: 36,
              height: 36,
              bgcolor: theme.palette.primary.dark,
              color: "#fff",
            }}
          >
            {getUserInitials()}
          </Avatar>
        )}
        <KeyboardArrowDownIcon
          sx={{
            ml: 0.5,
            color: "white",
            transition: "transform 0.2s",
            transform: open ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Box>

      {/* Dropdown menu */}
      <Menu
        id="profile-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        PaperProps={{
          elevation: 3,
          sx: {
            width: 220,
            mt: 1.5,
            overflow: "visible",
            filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.1))",
            "&:before": {
              content: '""',
              display: "block",
              position: "absolute",
              top: 0,
              right: 15,
              width: 10,
              height: 10,
              bgcolor: "background.paper",
              transform: "translateY(-50%) rotate(45deg)",
              zIndex: 0,
            },
          },
        }}
        transformOrigin={{ horizontal: "right", vertical: "top" }}
        anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
      >
        {/* User info */}
        <Box sx={{ px: 2, py: 1.5 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
            {user?.name || "User"}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {user?.email || "user@example.com"}
          </Typography>
        </Box>

        <Divider />

        {/* Menu items */}
        <MenuItem onClick={handleProfile}>
          <ListItemIcon>
            <AccountCircleIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>My Profile</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleSettings}>
          <ListItemIcon>
            <SettingsIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Settings</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleDashboard}>
          <ListItemIcon>
            <DashboardIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Dashboard</ListItemText>
        </MenuItem>

        <Divider />

        {/* Sign out */}
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: theme.palette.error.main }}>
            Sign Out
          </ListItemText>
        </MenuItem>

        {process.env.REACT_APP_VERSION && (
          <>
            <Divider />
            <Box sx={{ px: 2, py: 0.5 }}>
              <Typography variant="caption" color="text.disabled">
                v{process.env.REACT_APP_VERSION}
              </Typography>
            </Box>
          </>
        )}
      </Menu>
    </Box>
  );
};

export default ProfileDropDown;
